/**
 * KAAYA brand assets — pure-SVG, no external image deps.
 * Two exports:
 *   <TuhaIcon size={N} rounded={true} />          — square chat-bubble + pulse icon
 *   <TuhaWordmark size="sm|md|lg" tagline />      — icon + "KAAYA" wordmark (+ optional tagline)
 *
 * Palette (locked to KAAYA brand guide):
 *   Coral        #FF6B47
 *   Warm Orange  #FF8C42
 *   Deep Navy    #1A2744
 *   Soft White   #FAF8F5
 */

const NAVY = "#1A2744";

/**
 * KAAYA brand icon — the official logo mark (coral→orange speech-bubble with white
 * heartbeat pulse and green leaves). Rendered from /tuha-mark.png so it always matches
 * the favicon / app icon. Used for the in-app header, wordmark and inline marks.
 */
export function TuhaIcon({ size = 32, rounded = true, bgClass = "", title = "KAAYA" }) {
  return (
    <img
      src="/tuha-mark.png?v=tuha3"
      width={size}
      height={size}
      alt={title}
      className={bgClass}
      style={{ width: size, height: size, objectFit: "contain", display: "block" }}
      draggable={false}
    />
  );
}

/**
 * Brand wordmark. Renders the icon + "KAAYA" letterforms in coral→orange gradient.
 * Pure SVG so it never depends on a webfont being loaded.
 */
export function TuhaWordmark({ size = "md", tagline = false, className = "", inverse = false }) {
  const dims = {
    sm: { icon: 22, text: 22, gap: 8, tag: 10 },
    md: { icon: 28, text: 26, gap: 10, tag: 12 },
    lg: { icon: 40, text: 38, gap: 12, tag: 14 },
    xl: { icon: 56, text: 56, gap: 14, tag: 18 },
  }[size] || { icon: 28, text: 26, gap: 10, tag: 12 };

  return (
    <div className={`inline-flex items-center ${className}`} style={{ gap: dims.gap }} aria-label="KAAYA — your body, in balance">
      <TuhaIcon size={dims.icon} />
      <div className="flex flex-col leading-none">
        <svg
          height={dims.text}
          viewBox="0 0 200 56"
          xmlns="http://www.w3.org/2000/svg"
          role="img"
          aria-hidden="true"
          style={{ display: "block" }}
        >
          <text
            x="0"
            y="44"
            fontFamily="'Outfit', ui-sans-serif, system-ui, sans-serif"
            fontWeight="800"
            fontSize="52"
            letterSpacing="2"
            fill={inverse ? "#FAF8F5" : NAVY}
          >
            KAAYA
          </text>
        </svg>
        {tagline && (
          <span
            className={inverse ? "text-soft-white/85" : "text-deep-navy/80"}
            style={{
              fontFamily: "'Outfit', ui-sans-serif, system-ui, sans-serif",
              fontSize: dims.tag,
              letterSpacing: "0.02em",
              marginTop: 2,
            }}
          >
            your body, in balance
          </span>
        )}
      </div>
    </div>
  );
}

export default TuhaWordmark;
